import React, { createContext, useState, useContext, useCallback, useRef } from 'react';

const DebugContext = createContext();

export const DebugProvider = ({ children }) => {
  const [isDebugMode, setIsDebugMode] = useState(false);

  // Refs to store ID generation state and mappings without causing re-renders
  const idCounter = useRef([0, 0]); // Represents ['A', 'A'] indices
  const nameToIdMap = useRef({});
  const idToNameMap = useRef({}); // For potential reverse lookup if needed

  const toggleDebugMode = useCallback(() => {
    setIsDebugMode(prev => !prev);
    // Optionally reset IDs when toggling debug mode off? Or keep them persistent?
    // Resetting might be cleaner if component structure changes.
    if (!isDebugMode) { // Reset when turning ON debug mode
        console.log("Resetting Debug IDs");
        idCounter.current = [0, 0];
        nameToIdMap.current = {};
        idToNameMap.current = {};
    }
  }, [isDebugMode]);

  // Function to generate the next AA-ZZ ID
  const getNextId = useCallback(() => {
    const charCodeA = 'A'.charCodeAt(0);
    let firstLetterIndex = idCounter.current[0];
    let secondLetterIndex = idCounter.current[1];

    const id = String.fromCharCode(charCodeA + firstLetterIndex) + 
               String.fromCharCode(charCodeA + secondLetterIndex);

    // Increment logic
    secondLetterIndex++;
    if (secondLetterIndex > 25) {
      secondLetterIndex = 0;
      firstLetterIndex++;
      if (firstLetterIndex > 25) {
        console.error("Exceeded maximum debug IDs (ZZ). Resetting.");
        firstLetterIndex = 0; // Reset or handle overflow
        secondLetterIndex = 0;
      }
    }
    idCounter.current = [firstLetterIndex, secondLetterIndex];
    return id;
  }, []);

  // Function components call to get the displayable debug ID
  const getDebugId = useCallback((descriptiveName) => {
    if (!isDebugMode) return null; // Don't generate if not in debug mode
    if (!descriptiveName) return 'ERR'; // Need a name

    if (nameToIdMap.current[descriptiveName]) {
      // If ID already generated for this name, return it
      return nameToIdMap.current[descriptiveName];
    }

    // Generate a new ID
    const newId = getNextId();
    
    // Store the mappings
    nameToIdMap.current[descriptiveName] = newId;
    idToNameMap.current[newId] = descriptiveName;
    
    // Log the assignment for clarity during development/debugging
    console.log(`DEBUG ID Assigned: ${newId} -> ${descriptiveName}`);
    
    return newId; // Return the AA-ZZ code

  }, [isDebugMode, getNextId]);

  const value = {
    isDebugMode,
    toggleDebugMode,
    getDebugId,
  };

  return (
    <DebugContext.Provider value={value}>
      {children}
    </DebugContext.Provider>
  );
};

export const useDebug = () => useContext(DebugContext);
